import React, { useState, useEffect, useCallback } from 'react';
import { Save, Zap, AlertTriangle } from 'lucide-react';
import { apiMailerSettings, apiUpdateMailerSettings, apiMailerTick } from '../../api';
import DomainHealth from './DomainHealth';
import CapacityMeter from './CapacityMeter';
import { Field, Toggle, ActionButton, Toast, useToast, formatDateTime } from './parts';
import styles from './Mailer.module.css';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Asia/Karachi', 'Asia/Dubai', 'Asia/Kolkata',
  'Australia/Sydney', 'UTC',
];

/**
 * Global sending controls, transport status and the domain authentication audit.
 *
 * What is *not* here any more matters as much as what is. Daily volume, send
 * spacing and the sender identity all moved to the Mailboxes tab, because each
 * belongs to an address rather than to the installation. What is left is the
 * genuinely global: the kill switch, the opt-out wording, and the account-wide
 * circuit breaker.
 */
const SettingsTab = ({ jwtToken, mailboxes = [], onDisconnected }) => {
  const [settings, setSettings] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const report = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiMailerSettings(jwtToken);
      setSettings(data.settings);
      setCapacity(data.capacity || null);
    } catch (e) {
      setError(e.message || 'Could not load settings');
    } finally {
      setLoading(false);
    }
  }, [jwtToken]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError('');
    try {
      await apiUpdateMailerSettings(jwtToken, {
        sendingEnabled: settings.sendingEnabled,
        globalCapEnabled: settings.globalCapEnabled,
        globalDailyCap: settings.globalDailyCap,
        defaultMailboxDailyLimit: settings.defaultMailboxDailyLimit,
        timezone: settings.timezone,
        defaultFromAddress: settings.defaultFromAddress,
        defaultFromName: settings.defaultFromName,
        signatureHtml: settings.signatureHtml,
        unsubscribeText: settings.unsubscribeText,
        inboxSyncEnabled: settings.inboxSyncEnabled,
      });
      report.ok('Settings saved');
      await load();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };


  const runTick = async () => {
    setError('');
    try {
      const result = await apiMailerTick(jwtToken);
      report.ok(
        result.skipped
          ? `Nothing sent — ${result.skipped.replace(/_/g, ' ')}`
          : `Scheduler ran — ${result.totalSent || 0} message(s) sent`
      );
      await load();
    } catch (e) {
      report.fail(e);
      throw e;
    }
  };

  if (loading) return <div className="empty"><span className="spinner" style={{ width: 30, height: 30 }} /></div>;
  if (!settings) return <div className={styles.dangerBox}>{error || 'Settings unavailable'}</div>;

  const sendingDomain = (settings.defaultFromAddress || '').split('@')[1] || '';

  // Every domain with a live mailbox on it — each needs its own DNS records,
  // and a new domain with none is the most common reason a second sending
  // address quietly lands in spam.
  const sendingDomains = [...new Set(
    mailboxes.filter((m) => m.enabled).map((m) => m.domain).filter(Boolean)
  )].sort();

  // Zoho will happily "send as" a Gmail or Outlook address, but the message
  // still leaves Zoho's servers. Google publishes no SPF authorisation for
  // Zoho's IPs and cannot DKIM-sign for gmail.com, so alignment fails and the
  // mail is filtered on arrival — a guaranteed spam outcome for cold outreach.
  const FREE_MAIL = ['gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'yahoo.com', 'live.com', 'aol.com'];
  const usingFreeMail = FREE_MAIL.includes(sendingDomain.toLowerCase());

  return (
    <div>
      <Toast message={report.toast?.message} tone={report.toast?.tone} onDismiss={report.clear} />
      {error && <div className={styles.dangerBox}>{error}</div>}

      {capacity && <CapacityMeter totals={capacity} mailboxes={mailboxes} compact />}

      {/* ── Master switch ───────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 4 }}>Sending</h3>
        <p className="page-subtitle" style={{ marginBottom: 12 }}>
          The global controls. These sit above every campaign’s own settings.
        </p>

        {!settings.sendingEnabled && (
          <div className={styles.warnBox}>
            <div className={styles.boxTitle}><AlertTriangle size={16} /> Sending is switched off</div>
            No campaign will send while this is off, regardless of its own status. Turn it on when
            you are ready to go live.
          </div>
        )}

        <Toggle
          label="Enable sending"
          help="The master switch. Turning it off stops everything immediately without losing queue position."
          checked={settings.sendingEnabled}
          onChange={(v) => setSettings((s) => ({ ...s, sendingEnabled: v }))}
        />
        <Toggle
          label="Check for replies automatically"
          help="Polls your mailbox every few minutes and matches inbound mail back to campaigns."
          checked={settings.inboxSyncEnabled}
          onChange={(v) => setSettings((s) => ({ ...s, inboxSyncEnabled: v }))}
        />

        <Toggle
          label="Also enforce an account-wide daily ceiling"
          help={
            'Off by default, and it should usually stay off. Each mailbox already has a hard limit, '
            + 'and those are what protect your domains. This sits above all of them as a circuit '
            + 'breaker — useful if you want a single number you can drop to zero in a hurry, but a '
            + 'stale value here will silently throttle every mailbox with no error to explain it.'
          }
          checked={!!settings.globalCapEnabled}
          onChange={(v) => setSettings((s) => ({ ...s, globalCapEnabled: v }))}
        />

        <div className={styles.fieldRow} style={{ marginTop: 16 }}>
          {settings.globalCapEnabled && (
            <Field
              label="Account-wide daily ceiling"
              help={
                capacity?.dailyLimit
                  ? `Your mailboxes add up to ${capacity.dailyLimit}/day. A number below that throttles them all.`
                  : 'Total across every mailbox and campaign combined.'
              }
            >
              <input
                className="input" type="number" min="1" max="2000"
                value={settings.globalDailyCap}
                onChange={(e) => setSettings((s) => ({ ...s, globalDailyCap: Number(e.target.value) }))}
              />
            </Field>
          )}
          <Field
            label="Default limit for new mailboxes"
            help="Applied when a newly connected address is adopted. Lower it for a brand-new domain."
          >
            <input
              className="input" type="number" min="0" max="500"
              value={settings.defaultMailboxDailyLimit ?? 50}
              onChange={(e) => setSettings((s) => ({ ...s, defaultMailboxDailyLimit: Number(e.target.value) }))}
            />
          </Field>
          <Field label="Timezone" help="Used to decide when the account-wide counter resets.">
            <select
              className="input"
              value={settings.timezone}
              onChange={(e) => setSettings((s) => ({ ...s, timezone: e.target.value }))}
            >
              {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>

        <div className="text-sm text-muted" style={{ marginTop: 12 }}>
          {settings.lastTickAt && <>Scheduler last ran {formatDateTime(settings.lastTickAt)}.</>}
        </div>
        {settings.lastTickError && (
          <div className={styles.warnBox} style={{ marginTop: 12 }}>
            <div className={styles.boxTitle}><AlertTriangle size={16} /> Last scheduler run failed</div>
            {settings.lastTickError}
          </div>
        )}
      </div>

      {/* ── Identity ────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 16 }}>Sender identity</h3>
        <p className="page-subtitle" style={{ marginTop: -12, marginBottom: 16 }}>
          Fallbacks. Each mailbox can carry its own name and signature — set those on the Mailboxes
          tab, since a second domain usually exists precisely to be a different persona.
        </p>
        <div className={styles.fieldRow}>
          <Field label="Default from address" help="Used for portal notifications and as the fallback sender.">
            <select
              className="input"
              value={settings.defaultFromAddress}
              onChange={(e) => setSettings((s) => ({ ...s, defaultFromAddress: e.target.value }))}
            >
              <option value="">Choose an address…</option>
              {mailboxes.map((m) => <option key={m._id} value={m.address}>{m.address}</option>)}
            </select>
          </Field>
          <Field label="From name" help="A real person’s name outperforms a company name in cold outreach.">
            <input
              className="input"
              value={settings.defaultFromName}
              onChange={(e) => setSettings((s) => ({ ...s, defaultFromName: e.target.value }))}
              placeholder="Mustafa at Evercrest"
            />
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field
            label="Signature"
            help="Keep it plain text. Logos, banners and social icons make an email look like marketing, which is exactly what you want to avoid here."
          >
            <textarea
              className="input"
              style={{ minHeight: 80, resize: 'vertical' }}
              value={settings.signatureHtml}
              onChange={(e) => setSettings((s) => ({ ...s, signatureHtml: e.target.value }))}
              placeholder="Mustafa · Evercrest Publishing"
            />
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field
            label="Opt-out line"
            help="Appears at the foot of campaign mail alongside a one-click unsubscribe link. Giving people an easy exit is what keeps them off the Report Spam button."
          >
            <input
              className="input"
              value={settings.unsubscribeText}
              onChange={(e) => setSettings((s) => ({ ...s, unsubscribeText: e.target.value }))}
            />
          </Field>
        </div>
      </div>

      <ActionButton
        className="btn btn-primary"
        onClick={save}
        icon={<Save size={16} />}
        busyLabel="Saving…"
        doneLabel="Saved"
        failLabel="Not saved"
        style={{ marginBottom: 24 }}
      >
        Save settings
      </ActionButton>

      {/* ── Domain authentication ───────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {usingFreeMail && (
          <div className={styles.dangerBox}>
            <div className={styles.boxTitle}>
              <AlertTriangle size={16} /> Don’t send campaigns from {sendingDomain}
            </div>
            Zoho will send as this address, but the mail still leaves Zoho’s servers —
            and {sendingDomain} authorises neither Zoho’s IPs in SPF nor a DKIM key for them.
            Authentication fails on arrival, whatever your own domain’s records say. Pick an
            address on a domain you control.
          </div>
        )}
        <DomainHealth jwtToken={jwtToken} domain={sendingDomain} domains={sendingDomains} />
      </div>

      {/* ── Delivery ────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 className="card-title" style={{ marginBottom: 4 }}>Delivery method</h3>
        <p className="page-subtitle" style={{ marginBottom: 16 }}>
          Campaign mail and reply detection both run over the Zoho API — the same connection the
          portal already uses for client notifications. Nothing else to configure.
        </p>

        <div className={styles.toolbar} style={{ marginBottom: 0 }}>
          <ActionButton
            className="btn btn-ghost"
            onClick={runTick}
            icon={<Zap size={16} />}
            busyLabel="Running…"
            doneLabel="Ran"
            failLabel="Failed"
          >
            Run the scheduler now
          </ActionButton>
          <span className="text-sm text-muted">
            Releases any messages that are due, without waiting for the next tick.
          </span>
        </div>
      </div>

      <p className="text-sm text-muted" style={{ marginBottom: 0 }}>
        Connecting and disconnecting mail accounts now happens per login on the Mailboxes tab —
        disconnecting one there leaves the others sending.
      </p>
    </div>
  );
};

export default SettingsTab;
